<template>
  <div class="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-200 min-h-[3rem]">
    <!-- Mobile: left drawer toggle -->
    <button
      @click="store.leftDrawerOpen.value = true"
      class="md:hidden flex-shrink-0 text-gray-600 hover:text-gray-900"
      title="Open panels"
    ><PanelLeft :size="20" /></button>

    <!-- Selectors — allowed to shrink on mobile -->
    <div class="flex items-center gap-2 min-w-0 overflow-hidden">
      <!-- Project selector -->
      <div class="flex items-center gap-1 min-w-0">
        <select
          :value="store.currentProject.value?.id || ''"
          @change="onSelectProject"
          class="text-sm text-gray-900 border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:border-teal-500 min-w-0 max-w-[110px] md:max-w-none"
        >
          <option value="" disabled>Project...</option>
          <option v-for="p in store.projects.value" :key="p.id" :value="p.id">
            {{ p.name }}
          </option>
        </select>
        <button
          v-if="store.currentProject.value"
          @click="store.openEditPane('project')"
          class="flex-shrink-0 text-sm text-teal-600 hover:text-teal-800"
          title="Edit project"
        ><Pencil :size="14" /></button>
      </div>

      <!-- Author selector -->
      <div v-if="store.currentProject.value" class="flex items-center gap-1 min-w-0">
        <select
          :value="store.currentAuthor.value?.id || ''"
          @change="onSelectAuthor"
          class="text-sm text-gray-900 border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:border-teal-500 min-w-0 max-w-[110px] md:max-w-none"
        >
          <option value="" disabled>Author...</option>
          <optgroup v-if="projectAuthors.length" label="Assigned">
            <option v-for="a in projectAuthors" :key="'p-'+a.id" :value="a.id">{{ a.name }}</option>
          </optgroup>
          <optgroup v-if="unassignedAuthors.length" label="Other (auto-assign)">
            <option v-for="a in unassignedAuthors" :key="'u-'+a.id" :value="a.id">{{ a.name }} +</option>
          </optgroup>
        </select>
        <button
          v-if="store.currentAuthor.value"
          @click="store.openEditPane('author')"
          class="flex-shrink-0 text-sm text-teal-600 hover:text-teal-800"
          title="Edit author"
        ><Pencil :size="14" /></button>
        <button
          v-if="store.currentAuthor.value && isCurrentAuthorAssigned"
          @click="unassign"
          class="flex-shrink-0 text-sm text-gray-500 hover:text-red-500"
          title="Unassign author from project"
        ><UserMinus :size="14" /></button>
      </div>
    </div>

    <!-- Override badge -->
    <span
      v-if="overrideCount > 0"
      class="flex-shrink-0 text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded whitespace-nowrap"
    >{{ overrideCount }}</span>

    <!-- Spacer -->
    <div class="ml-auto flex-shrink-0 flex items-center gap-1">
      <div v-if="store.loading.value" class="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>

      <!-- Mobile: right drawer toggle (hidden on ideas/plan tabs — no right panel) -->
      <button
        v-if="store.activeTab.value !== 'ideas' && store.activeTab.value !== 'plan'"
        @click="store.rightDrawerOpen.value = true"
        class="md:hidden text-gray-600 hover:text-gray-900 p-1"
        title="Open brief & settings"
      ><PenSquare :size="20" /></button>
    </div>

    <!-- Hamburger menu -->
    <div class="relative flex-shrink-0" ref="menuRef">
      <button
        @click="menuOpen = !menuOpen"
        class="text-gray-600 hover:text-gray-900 p-1"
        title="Menu"
      ><Menu :size="20" /></button>
      <div
        v-if="menuOpen"
        class="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
      >
        <button
          @click="toggleTheme"
          class="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 text-left"
        >
          <Sun v-if="isDark" :size="16" />
          <Moon v-else :size="16" />
          <span>{{ isDark ? 'Light mode' : 'Dark mode' }}</span>
        </button>
        <hr class="my-1 border-gray-200">
        <a href="/projects" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" @click="menuOpen = false">Projects</a>
        <a href="/planner" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" @click="menuOpen = false">Planner</a>
        <a href="/api_token" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" @click="menuOpen = false">API Token</a>
        <a href="/users/edit" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" @click="menuOpen = false">Profile</a>
        <hr class="my-1 border-gray-200">
        <form action="/users/sign_out" method="post" class="px-4 py-2">
          <input type="hidden" name="_method" value="delete">
          <input type="hidden" name="authenticity_token" :value="csrfTokenValue">
          <button type="submit" class="text-sm text-gray-700 hover:text-red-600">Logout</button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, inject, onMounted, onUnmounted } from 'vue'
import { Pencil, UserMinus, PanelLeft, PenSquare, Menu, Sun, Moon } from 'lucide-vue-next'

const store = inject('workbench')

// Hamburger menu state
const menuOpen = ref(false)
const menuRef = ref(null)
const csrfTokenValue = document.querySelector('meta[name="csrf-token"]')?.content || ''
const isDark = ref(document.documentElement.classList.contains('dark'))

function toggleTheme() {
  const html = document.documentElement
  const nowDark = html.classList.toggle('dark')
  isDark.value = nowDark
  localStorage.setItem('theme', nowDark ? 'dark' : 'light')
}

function handleClickOutside(e) {
  if (menuOpen.value && menuRef.value && !menuRef.value.contains(e.target)) {
    menuOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))

const projectAuthorIds = computed(() =>
  (store.currentProject.value?.authors || []).map(a => a.id)
)

const projectAuthors = computed(() =>
  store.allAuthors.value.filter(a => projectAuthorIds.value.includes(a.id))
)

const unassignedAuthors = computed(() =>
  store.allAuthors.value.filter(a => !projectAuthorIds.value.includes(a.id))
)

const isCurrentAuthorAssigned = computed(() =>
  store.currentAuthor.value && projectAuthorIds.value.includes(store.currentAuthor.value.id)
)

const overrideCount = computed(() =>
  store.authorOverriddenFields.value.length + store.projectOverriddenFields.value.length
)

function onSelectProject(e) {
  const id = Number(e.target.value)
  if (id) store.selectProject(id)
}

function onSelectAuthor(e) {
  const id = Number(e.target.value)
  if (id) store.selectAuthor(id)
}

function unassign() {
  if (!store.currentAuthor.value) return
  store.unassignAuthorFromProject(store.currentAuthor.value.id)
}
</script>
